import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';

import { fDate, fToNow } from 'src/utils/format-time';

import { Iconify } from 'src/components/iconify';

// ----------------------------------------------------------------------

// Status color mapping
const STATUS_CONFIG = {
  Reviewing: {
    color: '#0288D1',
    variant: 'info',
    label: 'Reviewing',
    icon: 'solar:eye-bold-duotone',
  },
  Replied: {
    color: '#2E7D32',
    variant: 'success',
    label: 'Replied',
    icon: 'solar:check-circle-bold-duotone',
  },
  Closed: {
    color: '#637381',
    variant: 'default',
    label: 'Closed',
    icon: 'solar:close-circle-bold-duotone',
  },
};

// Feedback type icon mapping
const FEEDBACK_TYPE_ICONS = {
  'Bug Report': 'solar:bug-bold-duotone',
  'Feature Request': 'solar:lightbulb-bolt-bold-duotone',
  'General Feedback': 'solar:chat-round-dots-bold-duotone',
  'Security Concern': 'solar:shield-warning-bold-duotone',
  'Performance Issue': 'solar:speedometer-bold-duotone',
  'UI/UX Feedback': 'solar:palette-bold-duotone',
};

// ----------------------------------------------------------------------

export function FeedbackItem({ feedback, onClick }) {
  const statusConfig = STATUS_CONFIG[feedback.Status] || STATUS_CONFIG.Reviewing;
  const feedbackTypeIcon = feedback.FeedbackType?.Icon || 'solar:chat-round-dots-bold-duotone';
  const feedbackTypeIconColorCode = feedback.FeedbackType?.ColorCode || '#000000';
  const hasResponses = feedback.Replies && feedback.Replies.length > 0;

  const renderAvatar = (
    <ListItemAvatar>
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          bgcolor: `${statusConfig.color}15`,
          color: statusConfig.color,
        }}
      >
        <Iconify icon={feedbackTypeIcon} color={feedbackTypeIconColorCode} width={28} />
      </Stack>
    </ListItemAvatar>
  );

  const renderText = (
    <ListItemText
      disableTypography
      primary={
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {feedback.FeedbackType?.Name || 'Feedback'}
          </Typography>
          <Chip
            label={statusConfig.label}
            size="small"
            variant="filled"
            sx={{
              height: 20,
              fontSize: '0.7rem',
              bgcolor: `${statusConfig.color}20`,
              color: statusConfig.color,
              fontWeight: 600,
            }}
          />
          {hasResponses && (
            <Chip
              icon={<Iconify icon="solar:chat-line-bold-duotone" width={14} />}
              label={`${feedback.Replies.length} ${feedback.Replies.length === 1 ? 'Reply' : 'Replies'}`}
              size="small"
              variant="soft"
              color="success"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Stack>
      }
      secondary={
        <Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 1,
              mt: 0.5,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {feedback.Message}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ gap: 0.5 }}>
            <Chip
              icon={<Iconify icon="solar:calendar-bold-duotone" width={14} />}
              label={fDate(feedback.CreateDate)}
              size="small"
              variant="outlined"
              sx={{
                fontSize: '0.7rem',
                borderColor: `${statusConfig.color}30`,
                color: 'text.secondary',
              }}
            />
            <Typography variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
              {fToNow(feedback.CreateDate)}
            </Typography>
          </Stack>
        </Box>
      }
    />
  );

  return (
    <ListItemButton
      onClick={() => onClick && onClick(feedback)}
      sx={{
        p: 2.5,
        alignItems: 'flex-start',
        borderBottom: (theme) => `dashed 1px ${theme.vars.palette.divider}`,
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      {renderAvatar}
      <Stack sx={{ flexGrow: 1 }}>{renderText}</Stack>
    </ListItemButton>
  );
}

FeedbackItem.propTypes = {
  feedback: PropTypes.shape({
    Id: PropTypes.number,
    Message: PropTypes.string,
    CreateDate: PropTypes.string,
    Status: PropTypes.string,
    Replies: PropTypes.arrayOf(
      PropTypes.shape({
        Id: PropTypes.number,
        Message: PropTypes.string,
        CreateDate: PropTypes.string,
      })
    ),
    FeedbackType: PropTypes.shape({
      Id: PropTypes.number,
      Name: PropTypes.string,
      Description: PropTypes.string,
      SortId: PropTypes.number,
    }),
    FirstName: PropTypes.string,
    LastName: PropTypes.string,
    Role: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func,
};

